import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../Context/AuthContext';
import axios from 'axios';
import { Link, useHistory } from 'react-router-dom';
import { CircularProgress, Button } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import './StaffDashboard.css';
import noBookCover from '../../../assets/no-book-cover.png';
import config from '../../../config';

function StaffDashboard() {
    const { user, dispatch } = useContext(AuthContext);
    const [recentBooks, setRecentBooks] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const API_URL = config.API_URL;
    const history = useHistory();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Fetch recent books
                const booksRes = await axios.get(`${API_URL}api/books/allbooks`);
                setRecentBooks(booksRes.data.slice(0, 5));

                // Fetch transactions if user has permission
                if (user.permissions?.canManageBooks) {
                    const transactionsRes = await axios.get(`${API_URL}api/transactions/recent`);
                    setTransactions(transactionsRes.data);
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setLoading(false);
            }
        };

        if (user) {
            fetchDashboardData();
        } else {
            // If no user is found, redirect to login
            history.push('/signin');
        }
    }, [API_URL, user, history]);

    const handleLogout = () => {
        // Clear user from localStorage
        localStorage.removeItem("user");
        
        // Update the auth context
        dispatch({ type: "LOGOUT" });
        
        // Redirect to home page
        history.push('/');
    };

    if (loading) {
        return (
            <div className="staff-dashboard-loading">
                <CircularProgress />
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="staff-dashboard">
            <div className="staff-dashboard-header">
                <h1>Staff Dashboard</h1>
                <div className="staff-info">
                    <h2>Welcome, {user.userFullName}</h2>
                    <p>Role: {user.userType.charAt(0).toUpperCase() + user.userType.slice(1)}</p>
                    <p>ID: {user.employeeId}</p>
                    <p>Department: {user.department || 'Not specified'}</p>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ExitToAppIcon />}
                        onClick={handleLogout}
                        className="logout-button"
                    >
                        Logout
                    </Button>
                </div>
            </div>

            <div className="staff-dashboard-content">
                <div className="staff-permissions-section">
                    <h2>Your Permissions</h2>
                    <div className="permissions-list">
                        <div className={`permission-item ${user.permissions?.canUploadPDF ? 'active' : 'inactive'}`}>
                            <span className="permission-name">Upload PDF</span>
                            <span className="permission-status">{user.permissions?.canUploadPDF ? 'Enabled' : 'Disabled'}</span>
                        </div>
                        <div className={`permission-item ${user.permissions?.canManageUsers ? 'active' : 'inactive'}`}>
                            <span className="permission-name">Manage Users</span>
                            <span className="permission-status">{user.permissions?.canManageUsers ? 'Enabled' : 'Disabled'}</span>
                        </div>
                        <div className={`permission-item ${user.permissions?.canManageBooks ? 'active' : 'inactive'}`}>
                            <span className="permission-name">Manage Books</span>
                            <span className="permission-status">{user.permissions?.canManageBooks ? 'Enabled' : 'Disabled'}</span>
                        </div>
                    </div>
                </div>

                <div className="staff-actions-section">
                    <h2>Quick Actions</h2>
                    <div className="actions-grid">
                        <Link to="/books" className="action-card">
                            <div className="action-icon">📚</div>
                            <h3>Browse Books</h3>
                            <p>View and search the library collection</p>
                        </Link>

                        {user.permissions?.canUploadPDF && (
                            <Link to="/books" className="action-card">
                                <div className="action-icon">📄</div>
                                <h3>Upload PDFs</h3>
                                <p>Add digital versions of books</p>
                            </Link>
                        )}

                        {user.permissions?.canManageUsers && (
                            <Link to="/staff-management" className="action-card">
                                <div className="action-icon">👥</div>
                                <h3>Manage Staff</h3>
                                <p>Add and manage staff accounts</p>
                            </Link>
                        )}

                        {user.permissions?.canManageBooks && (
                            <Link to="/books" className="action-card">
                                <div className="action-icon">📝</div>
                                <h3>Manage Books</h3>
                                <p>Add, edit, or remove books</p>
                            </Link>
                        )}
                    </div>
                </div>

                <div className="recent-books-section">
                    <h2>Recently Added Books</h2>
                    <div className="recent-books-list">
                        {recentBooks.length > 0 ? (
                            recentBooks.map(book => (
                                <Link to={`/books/${book._id}`} key={book._id} className="recent-book-item">
                                    <div className="book-cover">
                                        <img 
                                            src={book.imageUrl || noBookCover} 
                                            alt={book.bookName}
                                            onError={(e) => { 
                                                e.target.onerror = null; 
                                                e.target.src = noBookCover; 
                                            }}
                                        />
                                    </div>
                                    <div className="book-info">
                                        <h3>{book.bookName}</h3>
                                        <p>By {book.author}</p>
                                        <p className="book-added-date">Added: {new Date(book.createdAt).toLocaleDateString()}</p>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <p className="no-data-message">No recent books found.</p>
                        )}
                    </div>
                </div>

                {user.permissions?.canManageBooks && (
                    <div className="recent-transactions-section">
                        <h2>Recent Transactions</h2>
                        <div className="transactions-list">
                            {transactions.length > 0 ? (
                                transactions.map(transaction => (
                                    <div key={transaction._id} className="transaction-item">
                                        <p><strong>Book:</strong> {transaction.bookName}</p>
                                        <p><strong>User:</strong> {transaction.userName}</p>
                                        <p><strong>Status:</strong> {transaction.transactionType}</p>
                                        <p><strong>Date:</strong> {new Date(transaction.createdAt).toLocaleDateString()}</p>
                                    </div>
                                ))
                            ) : (
                                <p className="no-data-message">No recent transactions found.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StaffDashboard; 